import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import {
  FileItem,
  listFilesV2,
  deleteFile as deleteFileApi,
  updateDisplayName as updateDisplayNameApi,
  listFolders
} from '../../../services/api'
import { contextualErrors, FriendlyError } from '../../../services/errorHandler'
import type { TranscriptionFolder } from '../../folders/types/folder.types'

export type SortOption = 'createdAt,desc' | 'createdAt,asc' | 'title,asc' | 'title,desc' | 'fileSize,desc' | 'fileSize,asc'

interface UseFilesOptions {
  pageSize?: number
  initialSort?: SortOption
}

interface UseFilesReturn {
  // Estado
  files: FileItem[]
  folders: TranscriptionFolder[]
  loading: boolean
  error: FriendlyError | null

  // Paginação
  currentPage: number
  totalPages: number
  totalFiles: number

  // Pasta atual
  currentFolderId: string | null
  currentFolderName: string

  // Ordenação
  sortOption: SortOption

  // Retry
  retryCountdown: number

  // Computed
  hasProcessing: boolean
  filteredFiles: FileItem[]

  // Actions
  fetchFiles: (page?: number, sort?: SortOption, silent?: boolean, folderId?: string | null) => Promise<void>
  deleteFile: (fileId: string) => Promise<boolean>
  renameFile: (fileId: string, newName: string) => Promise<boolean>
  setSearchQuery: (query: string) => void
  setSortOption: (sort: SortOption) => void
  setCurrentPage: (page: number) => void
  navigateToFolder: (folderId: string | null, folderName?: string) => void
  clearError: () => void
  setFolders: React.Dispatch<React.SetStateAction<TranscriptionFolder[]>>
}

export function useFiles(options: UseFilesOptions = {}): UseFilesReturn {
  const { pageSize = 24, initialSort = 'createdAt,desc' } = options

  // Estado principal
  const [files, setFiles] = useState<FileItem[]>([])
  const [folders, setFolders] = useState<TranscriptionFolder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<FriendlyError | null>(null)

  // Paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [totalFiles, setTotalFiles] = useState(0)

  // Pasta atual
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null)
  const [currentFolderName, setCurrentFolderName] = useState('')

  // Busca e ordenação
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOption, setSortOption] = useState<SortOption>(initialSort)

  // Retry automático
  const [retryCountdown, setRetryCountdown] = useState(0)
  const retryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Computed: filtro de busca
  const filteredFiles = useMemo(() => {
    if (!searchQuery.trim()) return files
    const query = searchQuery.trim().toLowerCase()
    return files.filter(f => f.title?.toLowerCase().includes(query))
  }, [files, searchQuery])

  // Computed: tem arquivos processando
  const hasProcessing = useMemo(
    () => files.some(f => f.status === 'processing'),
    [files]
  )

  // Função principal de carregamento
  const fetchFiles = useCallback(async (
    page = currentPage,
    sort = sortOption,
    silent = false,
    folderId: string | null | undefined = undefined
  ) => {
    if (!silent) setLoading(true)

    const effectiveFolderId = folderId !== undefined ? folderId : currentFolderId

    // Limpa contador de retry ao iniciar nova busca
    if (!silent) {
      setRetryCountdown(0)
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current)
        retryIntervalRef.current = null
      }
    }

    try {
      const [fileData, folderData] = await Promise.all([
        listFilesV2({ page: page - 1, size: pageSize, sort, folderId: effectiveFolderId, filterByFolder: true }),
        listFolders(effectiveFolderId).catch(() => [])
      ])

      setFiles(fileData.files || [])
      setTotalPages(fileData.totalPages || 0)
      setTotalFiles(fileData.totalFiles || 0)
      setCurrentPage(page)
      setFolders(folderData)

      // Sucesso: limpa erro
      setError(null)
      setRetryCountdown(0)
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current)
        retryIntervalRef.current = null
      }
    } catch (e: any) {
      const friendlyError = contextualErrors.listFiles(e)
      setError(friendlyError)
      setFiles([])

      // Inicia contador de retry
      if (!retryIntervalRef.current) {
        const RETRY_INTERVAL = 10
        setRetryCountdown(RETRY_INTERVAL)
        retryIntervalRef.current = setInterval(() => {
          setRetryCountdown(prev => (prev <= 1 ? RETRY_INTERVAL : prev - 1))
        }, 1000)
      } else {
        setRetryCountdown(10)
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [currentPage, sortOption, currentFolderId, pageSize])

  // Delete file
  const deleteFile = useCallback(async (fileId: string): Promise<boolean> => {
    try {
      await deleteFileApi(fileId)
      await fetchFiles(currentPage, sortOption, true)
      return true
    } catch (e: any) {
      const friendlyError = contextualErrors.delete(e)
      setError(friendlyError)
      return false
    }
  }, [fetchFiles, currentPage, sortOption])

  // Rename file (displayName)
  const renameFile = useCallback(async (fileId: string, newName: string): Promise<boolean> => {
    try {
      await updateDisplayNameApi(fileId, newName)
      // Atualiza o estado local sem recarregar a lista inteira
      setFiles(prev => prev.map(f =>
        f.fileId === fileId ? { ...f, displayName: newName } : f
      ))
      return true
    } catch (e: any) {
      const friendlyError = contextualErrors.listFiles(e)
      setError(friendlyError)
      return false
    }
  }, [])

  // Navegação para pasta
  const navigateToFolder = useCallback((folderId: string | null, folderName?: string) => {
    setCurrentFolderId(folderId)
    setCurrentPage(1)
    setSearchQuery('')
    setCurrentFolderName(folderName || '')
    fetchFiles(1, sortOption, false, folderId)
  }, [fetchFiles, sortOption])

  // Limpar erro
  const clearError = useCallback(() => setError(null), [])

  // Cleanup do retry interval
  useEffect(() => {
    return () => {
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current)
      }
    }
  }, [])

  return {
    files,
    folders,
    loading,
    error,
    currentPage,
    totalPages,
    totalFiles,
    currentFolderId,
    currentFolderName,
    sortOption,
    retryCountdown,
    hasProcessing,
    filteredFiles,
    fetchFiles,
    deleteFile,
    renameFile,
    setSearchQuery,
    setSortOption,
    setCurrentPage,
    navigateToFolder,
    clearError,
    setFolders
  }
}

export default useFiles
